import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ArrowLeft, AlertTriangle, X, Sparkles, Check, WifiOff, RefreshCw, Crown, Upload, ShieldAlert } from 'lucide-react'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useToast } from '@/hooks/use-toast'
import { useNavigationBlocker } from '@/hooks/use-navigation-blocker'
import { useAuth, type OrgTier } from '@/contexts/AuthContext'
import {
  createDropWithValidation,
  canCreateDrop,
  type TierType,
  type DropLimitError,
} from '@/lib/dropsStore'
import {
  isTrialAvailable,
  useTrialForDrop as markTrialUsedForDrop,
  TRIAL_PRO_FEATURES,
} from '@/lib/trialStore'
import { apiCreateDrop } from '@/lib/apiService'
import { DEMO_AUTH_ENABLED } from '@/lib/env'
import { useCreateDropMutation } from '@/data/drops'
import { useTrialQuery, useInvalidateTrial } from '@/data/trial'
import { PhotoSettings } from '@/components/PhotoSettings'
import { useUpsertDropPluginMutation, type PluginSettings, type ExifExtractorSettings } from '@/data/dropPlugins'

// Time limit options based on tier
const getTimeLimitOptions = (tier: OrgTier) => {
  const freeOptions = [
    { value: '24', label: '1 Day' },
    { value: '168', label: '1 Week' },
  ]

  const paidOptions = [
    { value: '24', label: '1 Day' },
    { value: '72', label: '3 Days' },
    { value: '168', label: '1 Week' },
    { value: '336', label: '2 Weeks' },
    { value: '504', label: '3 Weeks' },
    { value: '720', label: '1 Month' },
  ]

  return tier === 'free' ? freeOptions : paidOptions
}

interface FormErrors {
  name?: string
  timeLimit?: string
}

export default function CreateDropPage() {
  return DEMO_AUTH_ENABLED ? <DemoCreateDropPage /> : <RealCreateDropPage />
}

function DemoCreateDropPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { organization, hasPermission } = useAuth()
  const tier = organization?.tier || 'free'
  const canCreate = hasPermission('create_drop')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [timeLimit, setTimeLimit] = useState('24')
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Plugin settings state
  const [pluginSettings, setPluginSettings] = useState<PluginSettings>({})
  const [exifSettings, setExifSettings] = useState<ExifExtractorSettings>({})
  const isPaidTier = tier === 'pro' || tier === 'business'

  // Cover photo state
  const [coverPhoto, setCoverPhoto] = useState<File | null>(null)
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Free trial state
  const [trialAvailable] = useState(() => tier === 'free' && isTrialAvailable())
  const [useTrialDrop, setUseTrialDrop] = useState(false)

  // Network error state
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  // API/limit error state
  const [limitError, setLimitError] = useState<DropLimitError | null>(null)

  // Permission error state (403)
  const [permissionError, setPermissionError] = useState<string | null>(null)

  // Check if at drop limit on mount
  const dropLimitStatus = useMemo(() => canCreateDrop(tier as TierType), [tier])

  // Determine effective tier (Pro features if using trial)
  const effectiveTier = useTrialDrop ? 'pro' : tier
  const timeLimitOptions = useMemo(() => getTimeLimitOptions(effectiveTier), [effectiveTier])

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    return name.trim() !== '' || description.trim() !== ''
  }, [name, description])

  // Warn user before leaving page with unsaved changes (browser navigation)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges() && !isSubmitting) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges, isSubmitting])

  // Listen for network status changes
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      // Clear network error when back online
      if (networkError) {
        setNetworkError(null)
        toast({
          title: 'Connection restored',
          description: 'You\'re back online. You can now try again.',
        })
      }
    }

    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [networkError, toast])

  // Use custom navigation blocker for internal navigation
  const hasChanges = hasUnsavedChanges() && !isSubmitting
  const blocker = useNavigationBlocker(hasChanges)

  const validateField = (field: string, value: string): string | undefined => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Drop name is required'
        if (value.trim().length < 3) return 'Name must be at least 3 characters'
        if (value.trim().length > 100) return 'Name must be less than 100 characters'
        return undefined
      case 'timeLimit':
        if (!value) return 'Time limit is required'
        return undefined
      default:
        return undefined
    }
  }

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }))
    const value = field === 'name' ? name : timeLimit
    const error = validateField(field, value)
    setErrors(prev => ({ ...prev, [field]: error }))
  }

  const handleNameChange = (value: string) => {
    setName(value)
    if (touched.name) {
      const error = validateField('name', value)
      setErrors(prev => ({ ...prev, name: error }))
    }
  }

  // Cover photo handlers
  const handleCoverPhotoSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file (PNG, JPG, etc.)',
        variant: 'destructive',
      })
      return
    }

    // Max file size 10MB
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select an image under 10MB',
        variant: 'destructive',
      })
      return
    }

    setCoverPhoto(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      setCoverPhotoPreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleCoverPhotoSelect(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleCoverPhotoSelect(file)
    }
  }

  const handleRemoveCoverPhoto = () => {
    setCoverPhoto(null)
    setCoverPhotoPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Clear any previous errors
    setNetworkError(null)
    setLimitError(null)
    setPermissionError(null)

    // Validate all fields
    const newErrors: FormErrors = {
      name: validateField('name', name),
      timeLimit: validateField('timeLimit', timeLimit),
    }

    setErrors(newErrors)
    setTouched({ name: true, timeLimit: true })

    // If any errors, don't submit
    if (Object.values(newErrors).some(error => error)) {
      return
    }

    // Check if we're offline before attempting submission
    if (!navigator.onLine) {
      setNetworkError('Unable to create drop. Please check your internet connection and try again.')
      return
    }

    // Show loading state
    setIsSubmitting(true)

    try {
      // Simulate API call with network check
      // In a real app, this would be an actual API call that could throw on network failure
      await new Promise((resolve, reject) => {
        setTimeout(() => {
          // Check network status during the "API call"
          if (!navigator.onLine) {
            reject(new Error('Network connection lost'))
          } else {
            resolve(true)
          }
        }, 1000)
      })

      // Calculate expiration date
      const expiresAt = new Date(Date.now() + parseInt(timeLimit) * 60 * 60 * 1000)

      // First check permissions via API service (returns 403 for unauthorized roles)
      const apiResult = apiCreateDrop({
        name: name.trim(),
        description: description.trim() || undefined,
        status: 'active',
        photoCount: 0,
        contributorCount: 0,
        createdAt: new Date(),
        expiresAt,
        isTrial: useTrialDrop,
      }, undefined, tier)

      // Check for permission error (403)
      if (apiResult.status === 403) {
        setPermissionError(apiResult.error || 'You do not have permission to create drops.')
        toast({
          title: 'Permission Denied (403 Forbidden)',
          description: apiResult.error,
          variant: 'destructive',
        })
        setIsSubmitting(false)
        return
      }

      // Check for authentication error (401)
      if (apiResult.status === 401) {
        toast({
          title: 'Authentication Required',
          description: 'Please sign in to create drops.',
          variant: 'destructive',
        })
        navigate('/login')
        return
      }

      // Create the drop with tier validation (for limit checking)
      const result = createDropWithValidation(
        {
          name: name.trim(),
          description: description.trim() || undefined,
          status: 'active',
          photoCount: 0,
          contributorCount: 0,
          createdAt: new Date(),
          expiresAt,
          isTrial: useTrialDrop,
        },
        tier as TierType
      )

      // Check if creation failed due to limit
      if (!result.success) {
        setLimitError(result.error)
        toast({
          title: 'Drop limit reached',
          description: result.error.message,
          variant: 'destructive',
        })
        setIsSubmitting(false)
        return
      }

      // If using trial, record it
      if (useTrialDrop) {
        markTrialUsedForDrop(result.drop.id)
      }

      // Show success message
      toast({
        title: useTrialDrop ? 'Trial drop created!' : 'Drop created',
        description: useTrialDrop
          ? `"${name}" has been created with Pro features. Enjoy your free trial!`
          : `"${name}" has been created successfully.`,
      })

      navigate('/drops')
    } catch (error) {
      // Handle network error - preserve form data (don't clear anything)
      setNetworkError(
        'Unable to create drop. Please check your internet connection and try again.'
      )

      // Show toast as well for visibility
      toast({
        title: 'Connection error',
        description: 'Unable to create drop. Please check your internet connection.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-8 max-w-2xl">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Drops
      </button>

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold">Create Drop</h1>
        <p className="text-muted-foreground">Set up a new photo collection drop</p>
      </div>

      {/* Free Trial Card - only show to free tier users who haven't used trial */}
      {trialAvailable && (
        <div
          className={`bg-gradient-to-r ${
            useTrialDrop
              ? 'from-primary/10 to-primary/5 border-primary'
              : 'from-muted/50 to-muted border-border'
          } rounded-lg border-2 p-6 transition-all cursor-pointer`}
          onClick={() => setUseTrialDrop(!useTrialDrop)}
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${useTrialDrop ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
              <Sparkles className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Try Pro Features Free!</h3>
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    useTrialDrop
                      ? 'bg-primary border-primary text-white'
                      : 'border-border'
                  }`}
                >
                  {useTrialDrop && <Check className="h-4 w-4" />}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1 mb-3">
                Get one free drop with all Pro features. No credit card required.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {TRIAL_PRO_FEATURES.map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trial badge indicator when enabled */}
      {useTrialDrop && (
        <div className="flex items-center gap-2 text-sm text-primary bg-primary/10 px-4 py-2 rounded-lg">
          <Sparkles className="h-4 w-4" />
          <span>This drop will be created with Pro features (Free Trial)</span>
        </div>
      )}

      {/* Network Status Banner - shows when offline */}
      {!isOnline && (
        <div className="flex items-center gap-3 bg-accent/10 border border-accent/20 text-foreground px-4 py-3 rounded-lg">
          <WifiOff className="h-5 w-5 flex-shrink-0 text-accent" />
          <div>
            <p className="font-medium">You're offline</p>
            <p className="text-sm text-muted-foreground">Your changes will be saved when you're back online.</p>
          </div>
        </div>
      )}

      {/* Network Error Banner - shows after failed submission */}
      {networkError && isOnline && (
        <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/20 text-foreground px-4 py-3 rounded-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5 text-destructive" />
          <div className="flex-1">
            <p className="font-medium">Couldn't create drop</p>
            <p className="text-sm text-destructive">{networkError}</p>
          </div>
          <button
            type="button"
            onClick={() => setNetworkError(null)}
            className="text-destructive hover:text-destructive/90"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Permission Error Banner - shows 403 Forbidden for viewer role */}
      {permissionError && (
        <div className="bg-destructive/10 border border-destructive/20 text-foreground px-4 py-4 rounded-lg">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5 text-destructive" />
            <div className="flex-1">
              <p className="font-medium">403 Forbidden - Permission Denied</p>
              <p className="text-sm text-destructive mt-1">{permissionError}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Your current role ({organization?.role || 'unknown'}) does not have permission to create drops.
                Contact your organization owner or admin to request elevated permissions.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPermissionError(null)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Pre-emptive warning for viewer role (no permission) */}
      {!canCreate && !permissionError && (
        <div className="bg-destructive/10 border border-destructive/20 text-foreground px-4 py-4 rounded-lg">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5 text-destructive" />
            <div className="flex-1">
              <p className="font-medium">Permission Required</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your current role ({organization?.role}) does not have permission to create drops.
                Only owners, admins, and managers can create drops.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Drop Limit Error Banner - shows when at tier limit */}
      {limitError && (
        <div className="bg-accent/10 border border-accent/20 text-foreground px-4 py-4 rounded-lg">
          <div className="flex items-start gap-3">
            <Crown className="h-5 w-5 flex-shrink-0 mt-0.5 text-accent" />
            <div className="flex-1">
              <p className="font-medium">Drop limit reached</p>
              <p className="text-sm text-muted-foreground mt-1">{limitError.message}</p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <Link
                  to="/settings/billing"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90 transition-colors"
                >
                  <Crown className="h-4 w-4" />
                  Upgrade Plan
                </Link>
                <Link
                  to="/drops"
                  className="inline-flex items-center justify-center px-4 py-2 border border-border text-foreground text-sm font-medium rounded-md hover:bg-muted transition-colors"
                >
                  Manage Existing Drops
                </Link>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setLimitError(null)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Pre-emptive warning if already at limit */}
      {!dropLimitStatus.allowed && !limitError && (
        <div className="bg-accent/10 border border-accent/20 text-foreground px-4 py-4 rounded-lg">
          <div className="flex items-start gap-3">
            <Crown className="h-5 w-5 flex-shrink-0 mt-0.5 text-accent" />
            <div className="flex-1">
              <p className="font-medium">You've reached your drop limit</p>
              <p className="text-sm text-muted-foreground mt-1">
                You have {dropLimitStatus.currentCount} of {dropLimitStatus.limit} active drops on the {tier.charAt(0).toUpperCase() + tier.slice(1)} plan.
                Close an existing drop or upgrade to create more.
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <Link
                  to="/settings/billing"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90 transition-colors"
                >
                  <Crown className="h-4 w-4" />
                  Upgrade Plan
                </Link>
                <Link
                  to="/drops"
                  className="inline-flex items-center justify-center px-4 py-2 border border-border text-foreground text-sm font-medium rounded-md hover:bg-muted transition-colors"
                >
                  Manage Existing Drops
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-card text-card-foreground rounded-lg border p-6 space-y-6">
          {/* Drop Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Drop Name *
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => handleBlur('name')}
              placeholder="e.g., Sarah's Wedding"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.name && touched.name
                  ? 'border-destructive focus:ring-destructive'
                  : 'focus:ring-primary'
              }`}
              aria-invalid={errors.name && touched.name ? 'true' : 'false'}
              aria-describedby={errors.name && touched.name ? 'name-error' : undefined}
            />
            {errors.name && touched.name && (
              <p id="name-error" className="text-sm text-destructive mt-1">
                {errors.name}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details about your event..."
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Cover Photo */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Cover Photo
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
              id="cover-photo-input"
            />
            {coverPhotoPreview ? (
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={coverPhotoPreview}
                  alt="Cover preview"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-2 bg-card/90 text-card-foreground border border-border rounded-md text-sm font-medium hover:bg-card backdrop-blur-sm"
                  >
                    Change Photo
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveCoverPhoto}
                    className="px-3 py-2 bg-red-500 text-white rounded-md text-sm font-medium hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {coverPhoto?.name}
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary hover:bg-muted/50'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`p-3 rounded-full ${isDragging ? 'bg-primary/10' : 'bg-muted'}`}>
                    <Upload className={`h-6 w-6 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <p className="text-muted-foreground">
                    {isDragging ? 'Drop image here' : 'Drag and drop an image, or click to browse'}
                  </p>
                  <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB</p>
                </div>
              </div>
            )}
          </div>

          {/* Time Limit */}
          <div>
            <label htmlFor="timeLimit" className="block text-sm font-medium mb-2">
              Time Limit *
            </label>
            <select
              id="timeLimit"
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
              onBlur={() => handleBlur('timeLimit')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.timeLimit && touched.timeLimit
                  ? 'border-destructive focus:ring-destructive'
                  : 'focus:ring-primary'
              }`}
              aria-invalid={errors.timeLimit && touched.timeLimit ? 'true' : 'false'}
              aria-describedby={errors.timeLimit && touched.timeLimit ? 'timeLimit-error' : undefined}
            >
              {timeLimitOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {tier === 'free' && !useTrialDrop && (
              <p className="text-xs text-muted-foreground mt-1">
                Upgrade to Pro for custom time limits up to 1 month
              </p>
            )}
            {useTrialDrop && (
              <p className="text-xs text-green-600 mt-1">
                Pro time limits unlocked with your free trial!
              </p>
            )}
            {errors.timeLimit && touched.timeLimit && (
              <p id="timeLimit-error" className="text-sm text-destructive mt-1">
                {errors.timeLimit}
              </p>
            )}
          </div>
        </div>

        {isPaidTier && (
          <PhotoSettings
            processingSettings={pluginSettings}
            exifSettings={exifSettings}
            onProcessingChange={setPluginSettings}
            onExifChange={setExifSettings}
            disabled={isSubmitting}
          />
        )}

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || !isOnline || !dropLimitStatus.allowed || !canCreate}>
            {isSubmitting ? (
              'Creating...'
            ) : networkError || limitError ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </>
            ) : (
              'Create Drop'
            )}
          </Button>
        </div>
      </form>

      {/* Unsaved Changes Warning Modal */}
      {blocker.isBlocked && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => blocker.reset()}
          />

          {/* Modal */}
          <div className="relative bg-card text-card-foreground rounded-lg border shadow-lg max-w-md w-full mx-4 p-6 z-10">
            <button
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
              onClick={() => blocker.reset()}
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-3 text-orange-600 mb-4">
              <AlertTriangle className="h-6 w-6" />
              <h2 className="text-lg font-semibold">Unsaved Changes</h2>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.
            </p>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => blocker.reset()}
              >
                Stay on Page
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="flex-1"
                onClick={() => blocker.proceed()}
              >
                Leave Page
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function RealCreateDropPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { organization } = useAuth()
  const tier = organization?.tier || 'free'
  const { create } = useCreateDropMutation()
  const trialQuery = useTrialQuery(organization?.id)
  const invalidateTrial = useInvalidateTrial()
  const trialAvailable = tier === 'free' && trialQuery.data?.trialUsed === false

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [timeLimit, setTimeLimit] = useState('24')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [useTrialDrop, setUseTrialDrop] = useState(false)
  const [pluginSettings, setPluginSettings] = useState<PluginSettings>({})
  const [exifSettings, setExifSettings] = useState<ExifExtractorSettings>({})
  const upsertPluginMutation = useUpsertDropPluginMutation()
  // Trial drops get Pro-tier features (time limits, processing options).
  const effectiveTier: OrgTier = useTrialDrop ? 'pro' : tier
  const isPaidTier = effectiveTier === 'pro' || effectiveTier === 'business'

  const timeLimitOptions = useMemo(() => getTimeLimitOptions(effectiveTier), [effectiveTier])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!organization) {
      toast({
        title: 'No organization yet',
        description: 'Your account is still being set up. Please try again in a moment.',
        variant: 'destructive',
      })
      return
    }

    if (!name.trim()) {
      toast({ title: 'Name required', description: 'Please enter a drop name.', variant: 'destructive' })
      return
    }

    setIsSubmitting(true)
    try {
      const hours = Number(timeLimit)
      const { dropId } = await create({
        organizationId: organization.id,
        name: name.trim(),
        description: description.trim() || undefined,
        timeLimitHours: Number.isFinite(hours) ? hours : 24,
        isTrial: useTrialDrop,
      })
      if (useTrialDrop) {
        // Refresh trial availability so the UI immediately reflects that the
        // user's one free trial is now consumed by this drop.
        invalidateTrial(organization.id)
      }

      // Save plugin settings if configured (non-blocking — drop is already created)
      if (isPaidTier && Object.keys(pluginSettings).length > 0) {
        try {
          await upsertPluginMutation.mutateAsync({ dropId, pluginId: 'com.photodrop.pro-image-processor', settings: pluginSettings })
        } catch {
          toast({
            title: 'Drop created, but settings failed to save',
            description: 'You can configure processing settings on the edit page.',
            variant: 'destructive',
          })
        }
      }

      // Save EXIF settings if configured
      if (isPaidTier && Object.keys(exifSettings).length > 0) {
        try {
          await upsertPluginMutation.mutateAsync({
            dropId,
            pluginId: 'com.photodrop.exif-extractor',
            settings: exifSettings,
          })
        } catch {
          toast({
            title: 'Drop created, but EXIF settings failed to save',
            description: 'You can configure EXIF settings on the edit page.',
            variant: 'destructive',
          })
        }
      }

      toast({
        title: useTrialDrop ? 'Trial drop created!' : 'Drop created',
        description: useTrialDrop
          ? `"${name.trim()}" has been created with Pro features. Enjoy your free trial!`
          : 'Your drop is ready.',
      })
      navigate(`/drops/${dropId}`, { replace: true })
    } catch (err) {
      toast({
        title: 'Failed to create drop',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/drops">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Create Drop</h1>
          <p className="text-muted-foreground">Set up a new photo collection drop</p>
        </div>
      </div>

      {trialAvailable && (
        <button
          type="button"
          onClick={() => setUseTrialDrop(!useTrialDrop)}
          className={`w-full text-left rounded-lg border p-5 transition-colors ${
            useTrialDrop
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50 hover:bg-muted/30'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${useTrialDrop ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">Use your free trial</h3>
                {useTrialDrop && <Check className="h-4 w-4 text-primary" />}
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                One-time only: create this drop with Pro features at no charge.
              </p>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {TRIAL_PRO_FEATURES.map((feature) => (
                  <li key={feature} className="flex items-center gap-1">
                    <Check className="h-3 w-3 text-primary shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </button>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="bg-card text-card-foreground rounded-lg border p-6 space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Drop Name *
            </label>
            <input
              id="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Sarah's Wedding"
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details about your event..."
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label htmlFor="timeLimit" className="block text-sm font-medium mb-2">
              Time Limit *
            </label>
            <select
              id="timeLimit"
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {timeLimitOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {tier === 'free' && (
              <p className="text-xs text-muted-foreground mt-2">Upgrade to Pro for time limits up to 1 month.</p>
            )}
          </div>
        </div>

        {isPaidTier && (
          <PhotoSettings
            processingSettings={pluginSettings}
            exifSettings={exifSettings}
            onProcessingChange={setPluginSettings}
            onExifChange={setExifSettings}
            disabled={isSubmitting}
          />
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Drop'}
          </Button>
        </div>
      </form>
    </div>
  )
}
